// The e-mail deliverable read as a CAMPAIGN, not as one long document
// (Pavel 2026-08-05: "e-maily potrebuji vic sveho prostoru, lip se v tom
// vyznat, mozna tu kampan i lip rozkreslit").
//
// The generator already writes the campaign as a structure; the old reader
// threw that structure away. Every e-mail in an authored-HTML delivery doc is
// a PAIR inside a chapter:
//
//   <chapter title="Zvaci tyden">
//     <p>What this phase does in the campaign.</p>
//     <p><b>01. D-7, SEZNAM (note)</b><br/>Ukol: ...<br/>Nahradni predmety: A | B</p>
//     <fold title="Predmet: ...">  ...e-mail body...  </fold>
//
// The briefing paragraph carries the send day and the segment, the chapter
// carries the phase. Splitting only on <fold> (email-copy.ts, D12) tore the
// bodies out of their chapters and stacked all 21 at the end of the sheet,
// so the plan for e-mail 12 sat eighteen screens away from e-mail 12 itself.
//
// Nothing here invents a fact: a value is null when the document does not
// state it, and unrecognised `Label: value` lines survive as notes instead of
// being dropped or guessed at.

import { isDocHtml } from "../DocHtml";
import { splitEmailSections } from "../email-copy";

export interface CampaignNote {
  label: string;
  value: string;
}

export interface CampaignEmail {
  /** 1-based position across the whole campaign. */
  index: number;
  /** Order label the document itself uses ("01"), else the padded index. */
  order: string;
  subject: string;
  /** Send moment as written ("D-7", "platba plus 24 hodin"), null if unstated. */
  day: string | null;
  /** Recipient group ("SEZNAM"), null if unstated. */
  segment: string | null;
  /** Parenthetical behind the segment ("rodice z e-booku"), null if none. */
  segmentNote: string | null;
  /** What this e-mail has to achieve, one line. */
  task: string | null;
  altSubjects: string[];
  /** Everything else the briefing stated, label and value kept verbatim. */
  notes: CampaignNote[];
  /** Source fragment of the body for the document renderer. */
  body: string;
}

export interface CampaignPhase {
  /** Chapter title, null for e-mails that sit outside any chapter. */
  title: string | null;
  /** One sentence on what the phase does, null if the document has none. */
  summary: string | null;
  emails: CampaignEmail[];
}

export interface EmailCampaign {
  /** True when the body fragments are authored HTML, false for markdown. */
  html: boolean;
  /** Document standfirst, rendered above the timeline. */
  lead: string;
  phases: CampaignPhase[];
  /** Every e-mail in send order, flat. */
  emails: CampaignEmail[];
  /** What is left once the phases are lifted out: the strategy chapters. */
  intro: string;
}

const FOLD_RE = /<fold\b([^>]*)>([\s\S]*?)<\/fold>/gi;
const CHAPTER_RE = /<chapter\b([^>]*)>([\s\S]*?)<\/chapter>/gi;
const LEAD_RE = /<lead\b[^>]*>([\s\S]*?)<\/lead>/i;
// A briefing paragraph opens with a bold order label: `<p><b>01. ...</b>`.
const BRIEFING_RE = /<p>\s*<(?:b|strong)>\s*(\d+)\s*[.)]\s*([\s\S]*?)<\/(?:b|strong)>([\s\S]*?)<\/p>/gi;

const TASK_LABELS = ["ukol", "task", "cil", "goal"];
const ALT_SUBJECT_LABELS = ["nahradni predmety", "nahradni predmet", "alternative subjects", "alternative subject"];
const SUBJECT_PREFIX = /^\s*(?:p[rř]edm[eě]t|subject)\s*:\s*/i;

function attr(rawAttrs: string, name: string): string | null {
  const match = new RegExp(`${name}\\s*=\\s*"([^"]*)"`, "i").exec(rawAttrs);
  return match ? match[1].trim() || null : null;
}

function decodeEntities(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

/** Inline markup out, words in. Used for briefing fields and titles only. */
export function plainInline(source: string): string {
  return decodeEntities(source.replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();
}

/** Diacritics-insensitive key so a label matches whether or not the generator
 * wrote it with Czech accents. */
function labelKey(label: string): string {
  return label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

/** Commas OUTSIDE parentheses: "D+1 rano, SEZNAM (ucastnici i ti, kdo
 * nedorazili)" is TWO parts, not three. */
export function splitTopLevel(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const character of text) {
    if (character === "(") depth += 1;
    else if (character === ")") depth = Math.max(0, depth - 1);
    if (character === "," && depth === 0) {
      parts.push(current);
      current = "";
      continue;
    }
    current += character;
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter(Boolean);
}

/** `01. D-7, SEZNAM (rodice z e-booku)` -> day + segment + segment note. The
 * segment is the LAST top-level part; everything before it is the send moment,
 * which may itself carry commas ("posledni den okna, 7:00"). */
export function parseBriefingHead(head: string): Pick<CampaignEmail, "day" | "segment" | "segmentNote"> {
  const parts = splitTopLevel(plainInline(head));
  if (parts.length === 0) return { day: null, segment: null, segmentNote: null };
  if (parts.length === 1) return { day: parts[0], segment: null, segmentNote: null };
  const raw = parts[parts.length - 1];
  const day = parts.slice(0, -1).join(", ");
  const noted = /^([^(]+)\(([^)]*)\)\s*$/.exec(raw);
  return noted
    ? { day, segment: noted[1].trim(), segmentNote: noted[2].trim() || null }
    : { day, segment: raw, segmentNote: null };
}

/** The `Label: value` lines behind the head. Known labels become fields, the
 * rest survive verbatim - a generator that adds a line must never lose it. */
export function parseBriefingFields(body: string): Pick<CampaignEmail, "task" | "altSubjects" | "notes"> {
  const lines = body
    .split(/<br\s*\/?>/i)
    .map((line) => plainInline(line))
    .filter(Boolean);
  let task: string | null = null;
  const altSubjects: string[] = [];
  const notes: CampaignNote[] = [];
  for (const line of lines) {
    const separator = line.indexOf(":");
    if (separator < 0) {
      notes.push({ label: "", value: line });
      continue;
    }
    const label = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (!value) continue;
    const key = labelKey(label);
    if (TASK_LABELS.includes(key) && task === null) task = value;
    else if (ALT_SUBJECT_LABELS.includes(key)) altSubjects.push(...value.split("|").map((item) => item.trim()).filter(Boolean));
    else notes.push({ label, value });
  }
  return { task, altSubjects, notes };
}

/** Subject as a mail tool wants it: without the `Predmet:` label the reader
 * prints as a field caption. */
export function cleanSubject(title: string): string {
  return plainInline(title).replace(SUBJECT_PREFIX, "").trim();
}

interface FoldHit {
  title: string;
  body: string;
  briefing: { order: string; head: string; fields: string } | null;
  /** Source span of the fold plus its briefing, removed from the intro. */
  cut: Array<{ start: number; end: number }>;
}

/** Every fold in one source block, each with the briefing paragraph that sits
 * directly in front of it (the last one before the fold, nothing further). */
function foldsWithBriefings(source: string): FoldHit[] {
  const hits: FoldHit[] = [];
  let cursor = 0;
  FOLD_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = FOLD_RE.exec(source)) !== null) {
    const gap = source.slice(cursor, match.index);
    BRIEFING_RE.lastIndex = 0;
    let briefingMatch: RegExpExecArray | null;
    let last: { order: string; head: string; fields: string; start: number; end: number } | null = null;
    while ((briefingMatch = BRIEFING_RE.exec(gap)) !== null) {
      last = {
        order: briefingMatch[1],
        head: briefingMatch[2],
        fields: briefingMatch[3],
        start: cursor + briefingMatch.index,
        end: cursor + briefingMatch.index + briefingMatch[0].length
      };
    }
    const cut: Array<{ start: number; end: number }> = [{ start: match.index, end: match.index + match[0].length }];
    if (last) cut.unshift({ start: last.start, end: last.end });
    hits.push({
      title: attr(match[1], "title") ?? "",
      body: match[2].trim(),
      briefing: last ? { order: last.order, head: last.head, fields: last.fields } : null,
      cut
    });
    cursor = match.index + match[0].length;
  }
  return hits;
}

function toEmail(hit: FoldHit, index: number): CampaignEmail {
  const head = hit.briefing ? parseBriefingHead(hit.briefing.head) : { day: null, segment: null, segmentNote: null };
  const fields = hit.briefing
    ? parseBriefingFields(hit.briefing.fields)
    : { task: null, altSubjects: [], notes: [] as CampaignNote[] };
  return {
    index,
    order: hit.briefing ? hit.briefing.order.padStart(2, "0") : String(index).padStart(2, "0"),
    subject: cleanSubject(hit.title),
    ...head,
    ...fields,
    body: hit.body
  };
}

/** First paragraph of a phase chapter that is NOT a briefing: the sentence
 * that says what the phase does. */
function chapterSummary(inner: string, firstEmailAt: number): string | null {
  const before = inner.slice(0, firstEmailAt);
  const paragraphs = [...before.matchAll(/<p>([\s\S]*?)<\/p>/gi)]
    .filter((paragraph) => !/^\s*<(?:b|strong)>\s*\d+\s*[.)]/i.test(paragraph[1]))
    .map((paragraph) => plainInline(paragraph[1]))
    .filter(Boolean);
  return paragraphs[0] ?? null;
}

function cutSpans(source: string, spans: Array<{ start: number; end: number }>): string {
  let out = "";
  let cursor = 0;
  for (const span of [...spans].sort((a, b) => a.start - b.start)) {
    out += source.slice(cursor, span.start);
    cursor = Math.max(cursor, span.end);
  }
  return out + source.slice(cursor);
}

function buildFromDocHtml(source: string): EmailCampaign {
  const phases: CampaignPhase[] = [];
  const emails: CampaignEmail[] = [];
  const removals: Array<{ start: number; end: number }> = [];

  CHAPTER_RE.lastIndex = 0;
  let chapter: RegExpExecArray | null;
  while ((chapter = CHAPTER_RE.exec(source)) !== null) {
    const inner = chapter[2];
    const hits = foldsWithBriefings(inner);
    if (hits.length === 0) continue;
    // A chapter that carries e-mails becomes a PHASE and leaves the intro
    // whole: printing its title twice (phase header + strategy block) would
    // read as two different chapters saying the same thing.
    removals.push({ start: chapter.index, end: chapter.index + chapter[0].length });
    const base = emails.length;
    const phaseEmails = hits.map((hit, position) => toEmail(hit, base + position + 1));
    emails.push(...phaseEmails);
    const firstFoldAt = inner.search(/<fold\b/i);
    phases.push({
      title: attr(chapter[1], "title"),
      summary: attr(chapter[1], "takeaway") ?? chapterSummary(inner, firstFoldAt < 0 ? inner.length : firstFoldAt),
      emails: phaseEmails
    });
  }

  // Folds that never entered a chapter (older generator shapes) keep their
  // order and form one unnamed phase, so nothing silently disappears.
  const outsideSource = cutSpans(source, removals);
  const loose = foldsWithBriefings(outsideSource);
  let intro = outsideSource;
  if (loose.length > 0) {
    const looseEmails = loose.map((hit, position) => toEmail(hit, emails.length + position + 1));
    emails.push(...looseEmails);
    phases.push({ title: null, summary: null, emails: looseEmails });
    intro = cutSpans(outsideSource, loose.flatMap((hit) => hit.cut));
  }

  const lead = LEAD_RE.exec(intro);
  if (lead) intro = intro.replace(lead[0], "");
  return {
    html: true,
    lead: lead ? plainInline(lead[1]) : "",
    phases,
    emails,
    intro: intro.replace(/<chapter\b[^>]*>\s*<\/chapter>/gi, "").trim()
  };
}

/** Markdown deliveries (every document written before 2026-08-03) have no
 * phase structure to read, so they get one unnamed phase and no invented
 * days or segments. */
function buildFromMarkdown(source: string): EmailCampaign {
  const split = splitEmailSections(source);
  const emails: CampaignEmail[] = split.sections.map((section, position) => ({
    index: position + 1,
    order: String(position + 1).padStart(2, "0"),
    subject: cleanSubject(section.title),
    day: null,
    segment: null,
    segmentNote: null,
    task: null,
    altSubjects: [],
    notes: [],
    body: section.md
  }));
  return {
    html: false,
    lead: "",
    phases: emails.length > 0 ? [{ title: null, summary: null, emails }] : [],
    emails,
    intro: split.intro
  };
}

export function buildEmailCampaign(source: string): EmailCampaign {
  // A blueprint document (LAYOUT-BLUEPRINT-EMAILS) draws its own campaign
  // desk: <email> cards with copy fields and the <phase>/<tl> map. Reading
  // its folds as campaign e-mails would present a stray fold ("Druhá vlna")
  // as an e-mail, so the desk steps aside and the whole-document reader
  // renders the cards.
  if (isDocHtml(source) && /<email[\s>]/i.test(source)) {
    return { html: true, lead: "", phases: [], emails: [], intro: source };
  }
  return isDocHtml(source) ? buildFromDocHtml(source) : buildFromMarkdown(source);
}

/** "D-7 -> platba plus 24 hodin": the span the client reads in the header.
 * Null when the document states no days at all. */
export function campaignSpan(campaign: EmailCampaign): { from: string; to: string } | null {
  const days = campaign.emails.map((email) => email.day).filter((day): day is string => Boolean(day));
  if (days.length < 2) return null;
  return { from: days[0], to: days[days.length - 1] };
}

/** Which e-mails a phase holds, as the numbers printed on their own cards
 * ("01-07"). The phase heading used to print the order of its FIRST e-mail,
 * so phase three read as "08" and looked like chapter eight of something. */
export function phaseOrderRange(phase: CampaignPhase): string {
  if (phase.emails.length === 0) return "";
  const first = phase.emails[0].order;
  const last = phase.emails[phase.emails.length - 1].order;
  return first === last ? first : `${first}-${last}`;
}

/** Every e-mail of the campaign as one plain text block, in send order:
 * the briefing head the document itself wrote, the subject, the body. The
 * strategy chapters stay out - "copy all" sits on a campaign desk and the
 * client is copying MAIL, not the reasoning behind it. */
export function campaignPlainText(
  campaign: EmailCampaign,
  subjectLabel: string,
  toPlainText: (body: string) => string
): string {
  return campaign.emails
    .map((email) => {
      const head = [email.day, email.segment].filter(Boolean).join(", ");
      const lines = [head ? `${email.order}. ${head}` : `${email.order}.`];
      if (email.subject) lines.push(`${subjectLabel}: ${email.subject}`);
      lines.push("", toPlainText(email.body));
      return lines.join("\n").trim();
    })
    .join("\n\n---\n\n");
}
